import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { MapView }      from "./components/MapView.jsx";
import { Sidebar, DEFAULT_LISTING_FILTERS } from "./components/Sidebar.jsx";
import { Ranking }      from "./components/Ranking.jsx";
import { TownModal }    from "./components/TownModal.jsx";
import { ListingModal } from "./components/ListingModal.jsx";
import { api }          from "./api.js";
import { listNotedIds, getNote } from "./lib/notes.js";
import { DEFAULT_WEIGHTS, DEFAULT_SETTINGS } from "./lib/criteria.js";
import { nearestSchool, nearbySchools, driveTimeToScore, distanceToScore, loadDriveTimes } from "./lib/distance.js";
import { ComparePanel } from "./components/ComparePanel.jsx";
import { VisitPlanner } from "./components/VisitPlanner.jsx";
import { HomeGrid }     from "./components/HomeGrid.jsx";
import { PriorityWizard } from "./components/PriorityWizard.jsx";
import { CompareDashboard } from "./components/CompareDashboard.jsx";
import { IdentityGate } from "./components/IdentityGate.jsx";
import { getUser, canEditUser, USERS } from "./lib/user.js";

export default function App() {
  // ── State ──────────────────────────────────────────────────
  const [initial, setInitial]               = useState(null);
  const [view, setView]                     = useState("home");  // "home" | "map" | "list"
  const [listings, setListings]             = useState([]);
  const [listingFilters, setListingFilters] = useState(DEFAULT_LISTING_FILTERS);
  const [workflow, setWorkflow]             = useState({});
  const [weights, setWeights]               = useState(DEFAULT_WEIGHTS);
  const [enforceVeto, setEnforceVeto]       = useState(DEFAULT_SETTINGS.enforceVeto);
  const [waterVeto, setWaterVeto]           = useState(true);
  const [retreatMode, setRetreatMode]       = useState(DEFAULT_SETTINGS.retreatBusinessMode);
  const [selectedTown, setSelectedTown]     = useState(null);
  const [selectedListing, setSelectedListing] = useState(null);
  const [showPlanner, setShowPlanner] = useState(false);
  const [error, setError]                   = useState(null);
  const [leftOpen, setLeftOpen]             = useState(false);
  const [compareList, setCompareList]       = useState([]);
  const [topbarH, setTopbarH]               = useState(56);
  const [favorites, setFavorites]           = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("favTowns") || "[]")); }
    catch { return new Set(); }
  });
  const toggleFav = useCallback(key => {
    setFavorites(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      const arr = [...next];
      localStorage.setItem("favTowns", JSON.stringify(arr));
      api.putSetting("favorites", arr).catch(() => {});   // centraal + gedeeld
      return next;
    });
  }, []);
  // Bij opstarten: gedeelde favorieten van de server samenvoegen met lokale.
  useEffect(() => {
    api.getSetting("favorites").then(arr => {
      if (Array.isArray(arr)) {
        setFavorites(prev => {
          const merged = new Set([...prev, ...arr]);
          localStorage.setItem("favTowns", JSON.stringify([...merged]));
          return merged;
        });
      }
    }).catch(() => {});
  }, []);

  // Eenmalige migratie: bestaande lokale notities naar de server pushen.
  useEffect(() => {
    try { if (localStorage.getItem("notesMigrated")) return; } catch { return; }
    const ids = listNotedIds();
    Promise.all(ids.map(id => {
      const text = getNote(id);
      if (!text || !text.trim()) return Promise.resolve();
      const key = "note_" + String(id).replace(/[^a-z0-9]+/gi, "_");
      return api.getSetting(key).then(d => {
        if (d && typeof d.text === "string" && d.text.trim()) return; // server heeft al iets
        return api.putSetting(key, { text }).catch(() => {});
      }).catch(() => {});
    })).finally(() => { try { localStorage.setItem("notesMigrated", "1"); } catch {} });
  }, []);
  const openTownFromHome = useCallback(t => setSelectedTown(t), []);
  const [showWizard, setShowWizard] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [compareSel, setCompareSel] = useState(() => new Set());
  const toggleCompareSel = (name) => setCompareSel(prev => {
    const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n;
  });
  const [user, setUserState] = useState(() => getUser());
  const canEdit = canEditUser(user);
  const ptrStartY = useRef(null);
  const [ptrPull, setPtrPull] = useState(0);
  const [ptrRefreshing, setPtrRefreshing] = useState(false);
  const PTR_THRESHOLD = 70;
  const onPtrStart = e => { const el = e.currentTarget; ptrStartY.current = el.scrollTop <= 0 ? e.touches[0].clientY : null; };
  const onPtrMove  = e => {
    if (ptrStartY.current == null || ptrRefreshing) return;
    const dy = e.touches[0].clientY - ptrStartY.current;
    if (dy > 0) setPtrPull(Math.min(dy, 100));
  };
  const onPtrEnd = () => {
    if (ptrStartY.current == null) return;
    ptrStartY.current = null;
    if (ptrPull >= PTR_THRESHOLD) { setPtrRefreshing(true); setPtrPull(48); setTimeout(() => window.location.reload(), 400); }
    else setPtrPull(0);
  };
  useEffect(() => {
    try { if (!localStorage.getItem("onboarded") && canEditUser(getUser())) setShowWizard(true); } catch {}
  }, []);

  // ── Meet de echte topbar hoogte (fix bug #4: verkeerde selector) ──
  useEffect(() => {
    const el = document.querySelector(".top-bar");
    if (el) setTopbarH(el.offsetHeight);
  }, [initial]);

  // ── Data laden ─────────────────────────────────────────────
  useEffect(() => {
    api.initialState()
      .then(d => { setInitial(d); setWorkflow(d.workflow || {}); })
      .catch(e => setError(e.message));
    loadDriveTimes();
    api.listings().then(setListings).catch(() => {});
    api.getSetting("weights").then(w => w && setWeights({ ...DEFAULT_WEIGHTS, ...w }));
    api.getSetting("settings").then(s => {
      if (!s) return;
      if (typeof s.enforceVeto === "boolean")         setEnforceVeto(s.enforceVeto);
      if (typeof s.retreatBusinessMode === "boolean") setRetreatMode(s.retreatBusinessMode);
      if (typeof s.waterVeto === "boolean")           setWaterVeto(s.waterVeto);
    });
  }, []);

  // ── Keyboard: Escape sluit alles ───────────────────────────
  useEffect(() => {
    const h = e => {
      if (e.key === "Escape" && !selectedTown && !selectedListing) {
        setLeftOpen(false);
        setView("map");
      }
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [selectedTown, selectedListing]);

  // ── Callbacks ──────────────────────────────────────────────
  const updateWeights     = useCallback(w => { setWeights(w); api.putSetting("weights", w).catch(() => {}); }, []);
  const persistSettings   = useCallback(next => { api.putSetting("settings", next).catch(() => {}); }, []);
  const updateVeto        = useCallback(v => { setEnforceVeto(v); persistSettings({ enforceVeto: v, retreatBusinessMode: retreatMode, waterVeto }); }, [retreatMode, waterVeto, persistSettings]);
  const updateWaterVeto   = useCallback(v => { setWaterVeto(v); persistSettings({ enforceVeto, retreatBusinessMode: retreatMode, waterVeto: v }); }, [enforceVeto, retreatMode, persistSettings]);
  const updateRetreatMode = useCallback(v => { setRetreatMode(v); persistSettings({ enforceVeto, retreatBusinessMode: v, waterVeto }); }, [enforceVeto, waterVeto, persistSettings]);
  const updateWorkflow    = useCallback((url, state) => { setWorkflow(w => ({ ...w, [url]: state })); api.putWorkflow(url, state).catch(() => {}); }, []);

  // Bug #1 fix: handleSelectTown sluit geen rightOpen meer (dat bestaat niet meer)
  const handleSelectTown = useCallback(t => {
    setSelectedTown(t);
    setLeftOpen(false);
    setView("map");
  }, []);

  const toggleCompare = name => setCompareList(prev =>
    prev.includes(name) ? prev.filter(n => n !== name) : prev.length < 3 ? [...prev, name] : prev
  );

  // ── Score-berekening ───────────────────────────────────────
  const enrichedTowns = useMemo(() => {
    if (!initial) return null;
    return initial.towns.map(t => {
      const nearestMon = nearestSchool(t.coords, t.concelho, initial.institutions, "montessori");
      const nearestFor = nearestSchool(t.coords, t.concelho, initial.institutions, "forest-school");
      return {
        ...t,
        nearest_montessori:    nearestMon,
        nearest_forest_school: nearestFor,
        nearby_alt_schools:    nearbySchools(t.coords, initial.institutions),
        scores: {
          ...t.scores,
          montessori:   driveTimeToScore(nearestMon?.driveMin) || distanceToScore(nearestMon?.km),
          forestSchool: driveTimeToScore(nearestFor?.driveMin) || distanceToScore(nearestFor?.km),
        },
      };
    });
  }, [initial]);

  // Ruim verouderde favorieten op (oude concelho-sleutels die niet meer
  // op een plaatsnaam matchen), zodat telling en vergelijk-tabel kloppen.
  useEffect(() => {
    if (!enrichedTowns) return;
    const names = new Set(enrichedTowns.filter(Boolean).map(t => t.name));
    setFavorites(prev => {
      const cleaned = new Set([...prev].filter(n => names.has(n)));
      if (cleaned.size !== prev.size) {
        const arr = [...cleaned];
        try { localStorage.setItem("favTowns", JSON.stringify(arr)); } catch {}
        api.putSetting("favorites", arr).catch(() => {});
      }
      return cleaned;
    });
  }, [enrichedTowns]);

  // Deep-link: open een specifieke plaats uit ?plaats=Naam (bv. viewer-deel-link)
  useEffect(() => {
    if (!enrichedTowns) return;
    let name = null;
    try { name = new URLSearchParams(window.location.search).get("plaats"); } catch {}
    if (!name) return;
    const t = enrichedTowns.find(x => x && x.name === name);
    if (t) setSelectedTown(t);
  }, [enrichedTowns]);

  const filteredListings = useMemo(() => {
    const f = listingFilters;
    return listings.filter(l => {
      if (l.price_eur != null) {
        if (l.price_eur < f.priceMin) return false;
        if (f.priceMax < 3_000_000 && l.price_eur > f.priceMax) return false;
      }
      if (l.area_total_m2 != null) {
        if (l.area_total_m2 < f.areaMin) return false;
        if (f.areaMax < 1_000_000 && l.area_total_m2 > f.areaMax) return false;
      }
      const minBed = f.bedroomsMin ?? 0;
      if (minBed > 0 && l.bedrooms != null && l.bedrooms < minBed) return false;
      return true;
    });
  }, [listings, listingFilters]);

  // ── Render guards ──────────────────────────────────────────
  const concelhoBySlug = useMemo(
    () => Object.fromEntries((initial?.concelhos ?? []).map(c => [c.slug, c])),
    [initial]
  );

  if (error)                      return <div className="app-error">Fout: {error}</div>;
  if (!initial || !enrichedTowns) return <div className="app-loading">Portugal Locatie Verkenner laadt…</div>;

  const towns          = (enrichedTowns || []).filter(Boolean);
  const contentH       = `calc(100dvh - ${topbarH}px)`;

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100dvh", overflow:"hidden", background:"#faf8f4" }}>

      {!user && <IdentityGate onPick={setUserState} />}

      {/* ── Home: Airbnb-stijl landingsraster (overlay) ── */}
      {view === "home" && (
        <div onTouchStart={onPtrStart} onTouchMove={onPtrMove} onTouchEnd={onPtrEnd} onTouchCancel={onPtrEnd}
             style={{ position:"fixed", inset:0, zIndex:600, background:"#faf8f4", overflowY:"auto" }}>
          <div aria-hidden="true" style={{
            position:"absolute", top:0, left:0, right:0, display:"flex", alignItems:"flex-end",
            justifyContent:"center", height: ptrPull, overflow:"hidden", pointerEvents:"none", zIndex:5,
            transition: ptrStartY.current == null ? "height .25s ease" : "none",
          }}>
            <div style={{ marginBottom:10, width:32, height:32, borderRadius:"50%", background:"#fff",
                          boxShadow:"0 2px 8px rgba(0,0,0,.18)", display:"flex", alignItems:"center",
                          justifyContent:"center", fontSize:16, color:"#444" }}>
              <span className={ptrRefreshing ? "ptr-spin" : ""}
                    style={{ display:"inline-block",
                             transform: ptrRefreshing ? "none" : `rotate(${Math.min(ptrPull / PTR_THRESHOLD, 1) * 180}deg)`,
                             transition: ptrStartY.current == null ? "transform .2s ease" : "none" }}>
                {ptrRefreshing ? "⟳" : "↓"}
              </span>
            </div>
          </div>
          <div style={{ transform: ptrPull ? `translateY(${ptrPull}px)` : "none",
                        transition: ptrStartY.current == null ? "transform .25s ease" : "none" }}>
          <HomeGrid
            towns={towns} concelhoBySlug={concelhoBySlug} weights={weights}
            favorites={favorites} onToggleFav={toggleFav}
            onOpenTown={openTownFromHome}
            onShowMap={() => setView("map")}
            onOpenSearch={() => setShowWizard(true)}
            onOpenCompare={() => setShowCompare(true)}
            canEdit={canEdit}
            user={user}
            onSwitchUser={() => setUserState(null)}
            compareSel={compareSel}
            onToggleCompare={toggleCompareSel}
            enforceVeto={enforceVeto}
            waterVeto={waterVeto}
            onOpenCriteria={() => { setView("map"); setLeftOpen(true); }}
          />
          </div>
        </div>
      )}

      {/* Legacy TopBar vervangen door zwevende Airbnb-controls (zie onder) */}

      {/* Content rij */}
      <div style={{ display:"flex", flex:"1 1 0", minHeight:0, position:"relative" }}>

        {/* Bug #10 fix: backdrop — klik buiten de sidebar om te sluiten */}
        {leftOpen && (
          <div
            onClick={() => setLeftOpen(false)}
            style={{
              position:   "fixed",
              inset:      0,
              zIndex:     499,
              background: "rgba(0,0,0,0.35)",
              cursor:     "pointer",
            }}
            aria-hidden="true"
          />
        )}

        {/* Sidebar — fixed overlay links
            Bug #4 fix: topbarH gemeten via .top-bar (was: .topbar / header) */}
        <div style={{
          position:   "fixed",
          top:        0,
          left:       leftOpen ? 0 : -340,
          width:      340,
          height:     "100dvh",
          zIndex:     500,
          background: "#fff",
          boxShadow:  leftOpen ? "4px 0 24px rgba(0,0,0,.2)" : "none",
          transition: "left 0.25s ease",
          overflowY:  "auto",
          paddingTop: "calc(46px + env(safe-area-inset-top, 0px))",
          boxSizing:  "border-box",
        }}>
          <button
            onClick={() => setLeftOpen(false)}
            style={{
              position:   "absolute",
              top:        "calc(10px + env(safe-area-inset-top, 0px))",
              right:      10,
              zIndex:     10,
              background: "none",
              border:     "none",
              fontSize:   "22px",
              cursor:     "pointer",
              color:      "#444",
              lineHeight: 1,
            }}
            aria-label="Sluiten"
          >✕</button>
          <Sidebar
            isOpen={leftOpen}
            canEdit={canEdit}
            weights={weights}               onWeightsChange={updateWeights}
            enforceVeto={enforceVeto}       onVetoChange={updateVeto}
            listingFilters={listingFilters} onListingFiltersChange={setListingFilters}
            waterVeto={waterVeto}           onWaterVetoChange={updateWaterVeto}
            retreatMode={retreatMode}       onRetreatModeChange={updateRetreatMode}
          />
        </div>

        {/* Kaart — vult altijd de volledige content-area */}
        <div style={{ position:"absolute", inset:0, zIndex:1 }}>
          <MapView
            towns={towns}
            concelhoBySlug={concelhoBySlug}
            weights={weights}
            onOpenTown={handleSelectTown}
          />
        </div>

        {/* Lijst — overlay van rechts, alleen zichtbaar in list-view
            Bug #1 fix: isOpen altijd true want dit element bestaat alleen als view=list */}
        {view === "list" && (
          <div style={{
            position:      "absolute",
            top:           0,
            right:         0,
            width:         "min(480px, 100%)",
            height:        "100%",
            zIndex:        100,
            background:    "#faf8f4",
            boxShadow:     "-4px 0 24px rgba(0,0,0,.15)",
            display:       "flex",
            flexDirection: "column",
            overflow:      "hidden",
          }}>
            <Ranking
              listings={filteredListings}
              onSelectListing={setSelectedListing}
              isOpen={true}
              towns={towns}
              concelhoBySlug={concelhoBySlug}
              weights={weights}
              enforceVeto={enforceVeto}
              waterVeto={waterVeto}
              onSelectTown={handleSelectTown}
              compareList={compareList}
              onToggleCompare={toggleCompare}
            />
          </div>
        )}


        {/* Zwevende Airbnb-controls bovenaan de kaart */}
        {view !== "home" && (
          <div style={{
            position:"fixed", top:"calc(12px + env(safe-area-inset-top, 0px))", left:12, right:12, zIndex:400,
            display:"flex", justifyContent:"space-between", alignItems:"flex-start", pointerEvents:"none",
          }}>
            <button
              onClick={() => setView("home")}
              style={{
                pointerEvents:"auto", background:"#fff", color:"#333", border:"0.5px solid #ddd",
                borderRadius:22, padding:"9px 15px", fontSize:14, fontWeight:600, cursor:"pointer",
                boxShadow:"0 2px 10px rgba(0,0,0,.15)", display:"flex", alignItems:"center", gap:6,
                touchAction:"manipulation",
              }}
              aria-label="Terug naar overzicht"
            >← Overzicht</button>

            <div style={{ display:"flex", gap:8, pointerEvents:"auto" }}>
              <button
                onClick={() => setLeftOpen(o => !o)}
                style={{
                  width:44, height:44, borderRadius:"50%", background:"#fff", color:"#333",
                  border:"0.5px solid #ddd", boxShadow:"0 2px 10px rgba(0,0,0,.15)", fontSize:18,
                  cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
                  touchAction:"manipulation",
                }}
                aria-label="Criteria"
              >☰</button>
              <button
                onClick={() => setView(v => v === "list" ? "map" : "list")}
                style={{
                  width:44, height:44, borderRadius:"50%",
                  background: view === "list" ? "#222" : "#fff",
                  color: view === "list" ? "#fff" : "#333",
                  border: view === "list" ? "none" : "0.5px solid #ddd",
                  boxShadow:"0 2px 10px rgba(0,0,0,.15)", fontSize:18, cursor:"pointer",
                  display:"flex", alignItems:"center", justifyContent:"center", touchAction:"manipulation",
                }}
                aria-label={view === "list" ? "Toon kaart" : "Toon lijst"}
              >{view === "list" ? "🗺" : "📋"}</button>
            </div>
          </div>
        )}
      </div>

      {/* Vergelijkingsbalk */}
      <ComparePanel
        towns={compareList.map(name => towns.find(t => t.name === name)).filter(Boolean)}
        concelhoBySlug={concelhoBySlug}
        weights={weights}
        onRemove={name => setCompareList(prev => prev.filter(n => n !== name))}
        onOpenTown={handleSelectTown}
      />

      {showWizard && (
        <PriorityWizard onApply={updateWeights} onClose={() => setShowWizard(false)} />
      )}

      {showCompare && (
        <CompareDashboard
          towns={towns.filter(t => compareSel.has(t.name))}
          concelhoBySlug={concelhoBySlug}
          weights={weights}
          onOpenTown={(t) => { setShowCompare(false); setSelectedTown(t); }}
          onClose={() => setShowCompare(false)}
        />
      )}

      {/* Stad-modal */}

      {showPlanner && (
        <VisitPlanner
          towns={towns}
          concelhoBySlug={concelhoBySlug}
          weights={weights}
          enforceVeto={enforceVeto}
          onClose={() => setShowPlanner(false)}
        />
      )}

      {selectedTown && (
        <TownModal
          town={selectedTown}
          concelho={concelhoBySlug[selectedTown.concelho]}
          listings={listings}
          towns={towns}
          concelhoBySlug={concelhoBySlug}
          weights={weights}
          enforceVeto={enforceVeto}
          waterVeto={waterVeto}
          retreatMode={retreatMode}
          favorites={favorites}
          onToggleFav={toggleFav}
          canEdit={canEdit}
          currentUser={user}
          inCompare={compareSel.has(selectedTown.name)}
          onToggleCompare={toggleCompareSel}
          onClose={() => setSelectedTown(null)}
          onSelectListing={l => { setSelectedTown(null); setSelectedListing(l); }}
        />
      )}

      {/* Listing-modal */}
      {selectedListing && (
        <ListingModal
          listing={selectedListing}
          towns={towns}
          concelhoBySlug={concelhoBySlug}
          weights={weights}
          enforceVeto={enforceVeto}
          retreatMode={retreatMode}
          favorites={workflow}
          onWorkflowChange={updateWorkflow}
          canEdit={canEdit}
          onClose={() => setSelectedListing(null)}
        />
      )}
    </div>
  );
}